import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { InventoryBatch, StockMovement } from '../types/operations';
import { useLanguage } from '../contexts/LanguageContext';
import { newRequestId } from '../services/operationsService';
import MonthYearSelect from './MonthYearSelect';
import {
  DateTracking,
  DateField,
  dateGroups,
  endOfMonth,
  effectiveDates,
  fefoAllocations,
  trackingFields,
  tracksDates,
} from '../utils/inventoryDates';

export interface BatchSelection {
  mode: StockMovement['mode'];
  lots: InventoryBatch[];
  allocations: { batchId: string; quantity: number }[];
  initialized?: boolean;
}
export const emptySelection = (): BatchSelection => ({
  mode: 'fefo',
  lots: [],
  allocations: [],
});

export default function InventoryBatchPanel({
  batches,
  quantity,
  stockQuantity = 0,
  unit,
  storeId,
  equipmentId,
  type,
  value,
  onChange,
  unresolved = 0,
  tracking,
  alertDays = 90,
  expanded,
  onExpandedChange,
  hideToggle = false,
}: {
  batches: InventoryBatch[];
  quantity: number;
  stockQuantity?: number;
  unit: string;
  storeId: string;
  equipmentId: string;
  type?: 'add' | 'take' | 'transfer' | 'stocktaking';
  value?: BatchSelection;
  onChange?: (value: BatchSelection) => void;
  unresolved?: number;
  tracking: DateTracking;
  alertDays?: number;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  hideToggle?: boolean;
}) {
  const { t } = useLanguage();
  const tr = (key: string) => t('operations.' + key);
  const [localOpen, setLocalOpen] = useState(false);
  const [draftFields, setDraftFields] = useState<Record<string, DateField>>({});
  const [focusedGroup, setFocusedGroup] = useState<string | null>(null);
  const [selectedField, setField] = useState<'expiresOn' | 'reviewOn'>(
    'expiresOn',
  );
  if (!tracksDates(tracking)) return null;
  const fields = trackingFields(tracking);
  const field = fields.includes(selectedField) ? selectedField : fields[0];
  const open = expanded ?? localOpen;
  const editable = !!value && !!onChange;
  const receipt = type === 'add' || type === 'stocktaking';
  const unknownBatch = (
    count: number,
    date: string | null = null,
  ): InventoryBatch => ({
    id: newRequestId(),
    storeId,
    equipmentId,
    quantity: count,
    expiresOn: field === 'expiresOn' ? date : null,
    reviewOn: field === 'reviewOn' ? date : null,
  });
  const source = batches.map((batch) => effectiveDates(batch, tracking));
  const lots =
    editable && receipt
      ? value!.initialized
        ? value!.lots
        : type === 'add'
          ? [unknownBatch(quantity)]
          : source
      : source;
  const groups = dateGroups(lots, field);
  const visibleGroups = groups.filter(
    (group) =>
      type === 'add' ||
      group.quantity > 0 ||
      (editable &&
        receipt &&
        (focusedGroup === field + ':' + group.key ||
          group.items.some((batch) => draftFields[batch.id] === field))),
  );
  const picked =
    value?.mode === 'manual'
      ? value.allocations
      : fefoAllocations(source, quantity, tracking);
  const changeLots = (next: InventoryBatch[]) =>
    onChange?.({ ...value!, initialized: true, lots: next });
  const groupCount = (items: InventoryBatch[]) =>
    items.reduce(
      (n, b) => n + (picked.find((a) => a.batchId === b.id)?.quantity || 0),
      0,
    );
  const setCount = (group: (typeof groups)[number], count: number) => {
    if (!editable) return;
    const safe = Math.max(
      0,
      Math.min(Number.MAX_SAFE_INTEGER, Math.floor(count) || 0),
    );
    const ids = new Set(group.items.map((b) => b.id));
    if (safe > 0)
      setDraftFields((previous) =>
        Object.fromEntries(
          Object.entries(previous).filter(([id]) => !ids.has(id)),
        ),
      );
    if (!receipt) {
      const other = picked.filter((a) => !ids.has(a.batchId));
      let remaining = Math.min(
        safe,
        group.quantity,
        Math.max(0, stockQuantity - other.reduce((n, a) => n + a.quantity, 0)),
      );
      const allocations = group.items.flatMap((batch) => {
        const n = Math.min(batch.quantity, remaining);
        remaining -= n;
        return n ? [{ batchId: batch.id, quantity: n }] : [];
      });
      onChange?.({
        ...value!,
        mode: 'manual',
        initialized: true,
        allocations: [...other, ...allocations],
      });
      return;
    }
    let remaining = safe;
    const changed = group.items.map((batch) => {
      const n = Math.min(batch.quantity, remaining);
      remaining -= n;
      return { ...batch, quantity: n };
    });
    if (remaining || !changed.length)
      changed.push(
        unknownBatch(
          remaining,
          group.key
            ? endOfMonth(
                Number(group.key.slice(0, 4)),
                Number(group.key.slice(5, 7)),
              )
            : null,
        ),
      );
    changeLots([...lots.filter((b) => !ids.has(b.id)), ...changed]);
  };
  const setDate = (
    group: (typeof groups)[number],
    year: number,
    month: number,
  ) => {
    const ids = new Set(group.items.map((b) => b.id));
    changeLots(
      lots.map((batch) =>
        ids.has(batch.id)
          ? { ...batch, [field]: endOfMonth(year, month) }
          : batch,
      ),
    );
  };
  const addDate = () => {
    const date = new Date();
    while (
      groups.some(
        (g) =>
          (g.quantity > 0 ||
            g.items.some((batch) => draftFields[batch.id] === field)) &&
          g.key ===
            date.getFullYear() +
              '-' +
              String(date.getMonth() + 1).padStart(2, '0'),
      )
    )
      date.setMonth(date.getMonth() + 1, 1);
    const batch = unknownBatch(
      0,
      endOfMonth(date.getFullYear(), date.getMonth() + 1),
    );
    setDraftFields((previous) => ({ ...previous, [batch.id]: field }));
    changeLots([...lots, batch]);
  };
  const addUnknown = () => {
    const batch = unknownBatch(0);
    setDraftFields((previous) => ({ ...previous, [batch.id]: field }));
    changeLots([...lots, batch]);
  };
  const nearest = source
    .filter((b) => b.quantity > 0)
    .flatMap((b) => fields.map((f) => b[f]))
    .filter((d): d is string => !!d)
    .sort()[0];
  const today = new Date().toLocaleDateString('en-CA', {
    timeZone: 'Europe/Warsaw',
  });
  const approaching =
    nearest &&
    (Date.parse(nearest) - Date.parse(today)) / 86400000 <= alertDays;
  const toggle = () => {
    const next = !open;
    setLocalOpen(next);
    onExpandedChange?.(next);
  };
  return (
    <View style={s.panel}>
      {!hideToggle && (
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: open }}
          onPress={toggle}
          style={s.toggle}
        >
          <Text style={s.link}>
            {open ? '▾' : '▸'} {tr('datesAndBatches')}
          </Text>
          {!!nearest && (
            <Text style={approaching ? s.warning : s.hint}>
              {tr('nearestDate')}: {nearest.slice(0, 7)}
            </Text>
          )}
        </Pressable>
      )}
      {open && (
        <View style={s.body}>
          {fields.length > 1 && (
            <View style={s.tabs}>
              {fields.map((f) => (
                <Pressable
                  key={f}
                  onPress={() => setField(f)}
                  style={[s.tab, field === f && s.selected]}
                >
                  <Text>{tr(f)}</Text>
                </Pressable>
              ))}
            </View>
          )}
          {unresolved > 0 && (
            <Text style={s.warning}>
              {tr('unresolved')}: {unresolved} {unit}
            </Text>
          )}
          {visibleGroups.map((group, index) => {
            const count =
              editable && !receipt ? groupCount(group.items) : group.quantity;
            const max =
              editable && !receipt
                ? Math.min(
                    group.quantity,
                    stockQuantity -
                      picked.reduce((n, a) => n + a.quantity, 0) +
                      count,
                  )
                : undefined;
            return (
              <View
                key={group.key || 'unknown'}
                style={editable ? s.row : [s.row, s.readRow]}
              >
                {group.key ? (
                  !editable ? (
                    <Text style={s.readDate}>{group.key}</Text>
                  ) : (
                    <>
                      <MonthYearSelect
                        kind="year"
                        value={Number(group.key.slice(0, 4))}
                        label={tr('year') + ' ' + (index + 1)}
                        disabled={!editable || !receipt}
                        onChange={(year) =>
                          setDate(group, year, Number(group.key.slice(5, 7)))
                        }
                      />
                      <MonthYearSelect
                        kind="month"
                        value={Number(group.key.slice(5, 7))}
                        label={tr('month') + ' ' + (index + 1)}
                        disabled={!editable || !receipt}
                        onChange={(month) =>
                          setDate(group, Number(group.key.slice(0, 4)), month)
                        }
                      />
                    </>
                  )
                ) : (
                  <Text style={s.unknown}>{tr('unknownDates')}</Text>
                )}
                {editable ? (
                  <>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={
                        tr('decrease') + ' ' + (group.key || tr('unknownDates'))
                      }
                      disabled={count <= 0}
                      onPress={() => setCount(group, count - 1)}
                      style={[s.step, count <= 0 && s.disabled]}
                    >
                      <Text style={s.stepText}>−</Text>
                    </Pressable>
                    <TextInput
                      accessibilityLabel={
                        tr('quantity') + ' ' + (group.key || tr('unknownDates'))
                      }
                      style={s.quantity}
                      keyboardType="number-pad"
                      value={String(count)}
                      onFocus={() => setFocusedGroup(field + ':' + group.key)}
                      onBlur={() => setFocusedGroup(null)}
                      onChangeText={(raw) =>
                        setCount(group, Number(raw.replace(/[^0-9]/g, '')))
                      }
                    />
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={
                        tr('increase') + ' ' + (group.key || tr('unknownDates'))
                      }
                      disabled={max !== undefined && count >= max}
                      onPress={() => setCount(group, count + 1)}
                      style={[
                        s.step,
                        max !== undefined && count >= max && s.disabled,
                      ]}
                    >
                      <Text style={s.stepText}>+</Text>
                    </Pressable>
                  </>
                ) : (
                  <Text style={s.readCount}>
                    {count} {unit}
                  </Text>
                )}
              </View>
            );
          })}
          {editable && receipt && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={tr('addDate')}
              onPress={addDate}
              style={s.add}
            >
              <Text style={s.link}>+ {tr('addDate')}</Text>
            </Pressable>
          )}
          {editable && receipt && <Text style={s.hint}>{tr('monthEnd')}</Text>}
          {editable &&
            receipt &&
            !visibleGroups.some((group) => !group.key) && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={tr('addUnknownDates')}
                onPress={addUnknown}
                style={s.add}
              >
                <Text style={s.link}>+ {tr('unknownDates')}</Text>
              </Pressable>
            )}
        </View>
      )}
    </View>
  );
}
const s = StyleSheet.create({
  panel: { backgroundColor: '#F8FAFC', borderRadius: 8 },
  toggle: { padding: 10 },
  link: { color: '#2563EB', fontWeight: '600', fontSize: 13 },
  body: { paddingVertical: 8, paddingHorizontal: 4, gap: 6 },
  hint: { color: '#64748B', fontSize: 11 },
  warning: { color: '#B45309', fontSize: 12 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 4, minHeight: 34 },
  readRow: { minHeight: 24 },
  readDate: { flex: 2, color: '#475569', fontSize: 13, paddingHorizontal: 6 },
  unknown: { flex: 2, color: '#475569', fontSize: 13, paddingHorizontal: 6 },
  step: {
    width: 32,
    height: 34,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    backgroundColor: '#EFF6FF',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepText: { color: '#334155', fontSize: 18 },
  quantity: {
    width: 44,
    height: 34,
    padding: 2,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 6,
    backgroundColor: '#FFF',
    textAlign: 'center',
    color: '#1E293B',
    fontSize: 14,
  },
  disabled: { opacity: 0.4 },
  readCount: {
    width: 90,
    textAlign: 'right',
    fontWeight: '600',
    color: '#334155',
  },
  add: { paddingVertical: 8 },
  tabs: { flexDirection: 'row', gap: 8, marginBottom: 4 },
  tab: { padding: 6, borderRadius: 6 },
  selected: { backgroundColor: '#DBEAFE' },
});
